import { IonButton, IonIcon, IonItem, useIonAlert, IonModal, IonHeader, IonToolbar, IonTitle, IonContent, IonList, IonRadioGroup, IonRadio, IonInput, IonLabel, useIonLoading } from '@ionic/react';
import { cloudUpload } from 'ionicons/icons';
import React, { useState, useRef } from 'react';
import { UploadAppDoc, UploadAppDocX } from '../data/api';
import { GetDailyCode } from '../data/utilities';
import './Generic.css';
import useAuth from '../data/useAuth';

interface CameraProps {
  useremail: any;
  setRefresh: any;
  existingFiles?: any[];
  isLoadingFiles?: boolean;
}

const Camera: React.FC<CameraProps> = ({ useremail, setRefresh, existingFiles = [], isLoadingFiles = false }) => {
  
  let emailaddress = useremail;
  const { user, setUser } = useAuth();
  const [present] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<any>("");
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [resolutionMode, setResolutionMode] = useState<'rename' | 'version'>('version');
  const [newFileName, setNewFileName] = useState("");

  const findNextVersionName = (baseName: string, files: any[]) => {
    const ext = baseName.split('.').pop();
    const nameWithoutExt = baseName.substring(0, baseName.lastIndexOf('.'));

    let version = 1;
    let nextName = `${nameWithoutExt} (${version}).${ext}`;

    const checkExists = (name: string) => files.some(f => f.filename === name);
    while (checkExists(nextName)) {
      version++;
      nextName = `${nameWithoutExt} (${version}).${ext}`;
    }
    return nextName;
  };

  const executeUpload = async (fileToUpload: any, fileName: string) => {
    await presentLoading({
      message: 'Uploading document...',
    });
    setRefresh(false);
    const formData = new FormData();
    if (user === null || user === '') {
      formData.append("dailycode", GetDailyCode(emailaddress));
    }
    else {
      formData.append("dailycode", user);
    }
    formData.append("email", emailaddress);
    formData.append("fileName", fileName);
    formData.append("formFile", fileToUpload);

    let myPromise = new Promise(function (myResolve, myReject) {
      if (user === null || user === '') {
        UploadAppDoc(formData).then(() => myResolve("Ok")).catch((err) => myReject("Error:" + err));
      }
      else {
        UploadAppDocX(formData).then(() => myResolve("Ok")).catch((err) => myReject("Error:" + err));
      }
    });

    myPromise.then(
      async function (value: any) { 
        await dismissLoading();
        setRefresh(true); 
        setShowDuplicateModal(false);
        setFile("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
      async function (error: any) { 
        await dismissLoading();
        present(error); 
      }
    );
  };

  // Handles file upload event and updates state
  const handleUpload = (event: any) => {
    setFile(event.target.files[0]);
  }

  const handleFileSubmission = (event: any) => {

    if (file !== "") {
      if (file.name.split('.').pop().toLowerCase() === "pdf" || file.name.split('.').pop().toLowerCase() === "jpg" || file.name.split('.').pop().toLowerCase() === "jpeg") {
        
        const isDuplicate = existingFiles && existingFiles.some(f => f.filename === file.name);
        if (isDuplicate) {
          setNewFileName(file.name);
          setShowDuplicateModal(true);
        } else {
          executeUpload(file, file.name);
        }
      }
      else {
        present("We only accept PDF file or JPG file!");
      }
    }
    else {
      present("Choose the file first!");
    }
  }

  const handleResolveDuplicate = () => {
    if (resolutionMode === 'version') {
      const nextName = findNextVersionName(file.name, existingFiles);
      executeUpload(file, nextName);
    } else {
      executeUpload(file, newFileName);
    }
  };

  return (<>

      


      <IonItem color="medium" >
        <div>
        <IonIcon icon={cloudUpload} size="large" slot="start"></IonIcon> &nbsp; &nbsp;
        Please upload your <strong>cover letter, resume</strong> and any other files which you want to submit with
        your application. If you have claimed <strong>Veteran's Preference</strong>, please
          upload your DD-214 form. Click [Choose File] first, then click [Upload].
          You can use url as well (e.g. https://www.irs.gov/pub/irs-pdf/fw4sp_20.pdf ).  <strong>Please do NOT name your
            file with the following characters [ \ / : * ? " &#62; &#60;  ]  characters</strong>.
        </div>       
      </IonItem>
      <IonItem lines="none" color="transparent" >
        <input type="file" onChange={handleUpload} accept="application/pdf, image/jpeg" ref={fileInputRef} />
        <IonButton style={{ height: '40px', width: '180px' }}
          slot="end" color="primary"
          onClick={handleFileSubmission}
          disabled={isLoadingFiles}
        ><strong>upload</strong>
        </IonButton>
      </IonItem>

      <IonModal isOpen={showDuplicateModal} onDidDismiss={() => setShowDuplicateModal(false)}>
        <IonHeader>
          <IonToolbar color="warning">
            <IonTitle>File Already Exists</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <p>A file named <strong>{file.name}</strong> has already been uploaded. How would you like to proceed?</p>
          
          <IonList>
            <IonRadioGroup value={resolutionMode} onIonChange={e => setResolutionMode(e.detail.value)}>
              <IonItem>
                <IonLabel>Rename the file</IonLabel>
                <IonRadio slot="start" value="rename" />
              </IonItem>
              <IonItem>
                <IonLabel>Append a version number</IonLabel>
                <IonRadio slot="start" value="version" />
              </IonItem>
            </IonRadioGroup>
          </IonList>

          {resolutionMode === 'rename' && (
            <IonItem>
              <IonLabel position="floating">New File Name</IonLabel>
              <IonInput 
                value={newFileName} 
                onIonChange={e => setNewFileName(e.detail.value!)}
              />
            </IonItem>
          )}

          <div className="ion-padding-top duplicate-file-buttons">
            <IonButton 
              expand="block" 
              onClick={handleResolveDuplicate}
              disabled={resolutionMode === 'rename' && (newFileName === file.name || (existingFiles && existingFiles.some(f => f.filename === newFileName)))}
            >
              {resolutionMode === 'rename' ? 'Apply & Upload' : 'Append & Upload'}
            </IonButton>
            <IonButton expand="block" color="light" onClick={() => setShowDuplicateModal(false)}>
              Cancel
            </IonButton>
          </div>
        </IonContent>
      </IonModal>
    </>)
  }


export default Camera;

